import React, { forwardRef, useEffect, useState } from "react";
import styled from "styled-components";
import IEditableProps from "../../../utilities/IEditableProps";
import IStyledProps from "../../../utilities/IStyledProps";
import classNames from "classnames";
import { AutomationTestIdFeature, AutomationTestIdElementType, createDataTestId } from "../../../utilities/automation";
import cx from "classnames";
import Picture from "../../Picture";
import { useIsMobile } from "../../../context/MobileContext";
import { getImagePath } from "../../../utilities/path";
import renderContent from "../../../utilities/renderContent";

export interface IHeroFullHeightProps extends IEditableProps, IStyledProps {
    video: string;
    image: string;
    alt: string;
    gradientClass: string;
    underhang: boolean;
    heroSubTitle;
    heroTitle;
    heroBody;
    heroFooter;
    children;
}

const HeroFullHeightContainer = styled.div`

    .hero-title {
        font-size: 3.33333rem;
        font-weight: 350;
        line-height: 1.2;
        letter-spacing: -0.6px;
        @media (max-width: 991px) {
            font-size: 2.22222rem;
        }
    }

    .hero-sub-title {
        letter-spacing: 0.02667rem;
        text-transform: uppercase;
    }

    .hero-body, .hero-footer {
        font-size: 0.88889rem;
        font-weight: 400;
        line-height: 1.44444rem; /* 130% */
    }
`;

const HeroFullHeight = forwardRef((props: IHeroFullHeightProps, ref: any) => {
    const { isMobileWidth } = useIsMobile();
    const [showVideo, setShowVideo] = useState(false);
    const { children, heroSubTitle, heroTitle, heroBody, heroFooter, gradientClass, image, alt, video, editorClassObj, className, cmsId } = props;
    const hasChildren = Boolean(children);
    const hasVideo = Boolean(video);

    useEffect(() => {
        // When useIsMobile runs in gatsby it will return null, which is falsy but not false
        // So we wait until we get a real boolean value to see if the screen is really mobile
        if (isMobileWidth === false) {
            setShowVideo(true);
        }
    }, [isMobileWidth]);

    return (
        <HeroFullHeightContainer data-id={cmsId} ref={ref} className={classNames(className, editorClassObj, gradientClass, "text-white no-border bg-white")}>
            <div className="container content border-0">
                <div className="row">
                    <div className={cx("d-flex col-12 order-lg-2 p-0 overflow-hidden", {"col-lg-6": !hasChildren}, {"col-lg-4": hasChildren})}>
                        {(showVideo && hasVideo) ?
                            <video muted loop autoPlay
                                className="w-100 h-100 object-fit-cover d-none d-lg-block"
                                poster={getImagePath(image)}
                                data-test-id={createDataTestId({ feature: AutomationTestIdFeature.HERO, element: AutomationTestIdElementType.VIDEO, specifier: 'full' })}>
                                <source src={getImagePath(video)} type="video/mp4" />
                                <Picture
                                    src={getImagePath(image)}
                                    alt={alt}
                                    className="w-100 h-100 object-fit-cover"
                                    dataTestId={createDataTestId({ feature: AutomationTestIdFeature.HERO, element: AutomationTestIdElementType.IMAGE, specifier: 'fallback' })}
                                    />
                            </video>
                            :
                            null
                        }
                        <Picture
                            pictureClassName="w-100"
                            src={getImagePath(image)}
                            alt={alt}
                            className={classNames("w-100 h-100 object-fit-cover", { "d-lg-none": hasVideo })}
                            dataTestId={createDataTestId({ feature: AutomationTestIdFeature.HERO, element: AutomationTestIdElementType.IMAGE, specifier: 'mobile' })}/>
                    </div>

                    <div className="col-lg-6 col-md-9 col-12 px-4 py-5 p-md-5 p-lg-6 py-xl-8 my-auto order-lg-1">
                        <h2 className="hero-sub-title">
                            {renderContent("HeroFullHeight", heroSubTitle)}
                        </h2>
                        <h1 className="hero-title mb-4">
                            {renderContent("HeroFullHeight", heroTitle)}
                        </h1>
                        <div className="hero-body mb-lg-6 mb-5">
                            {renderContent("HeroFullHeight", heroBody)}
                        </div>
                        <div className="hero-footer">
                            {renderContent("HeroFullHeight", heroFooter)}
                        </div>
                    </div>

                    {hasChildren ? (
                        React.Children.map(children, (card, cardIndex) => {
                            return React.cloneElement(card, {
                                className: cx("col-12 col-md-3 col-lg-2 col-4 order-lg-3 d-flex flex-row-reverse flex-md-column justify-content-end"),
                                cardIndex,
                            });
                        })) : null
                    }
                </div>
            </div>
        </HeroFullHeightContainer>
    )
});

export default HeroFullHeight;